import Tabs from "common/Tabs/Tabs";
import React, { useContext } from "react";
import Title from "common/Typography/Title";
import Flex from "common/Boxes/Flex";
import { ArrowRightOutlined } from "common/Icons/Icons";
import styled, { useTheme } from "styled-components";
import NewLookConnectExchange from "features/portfolio/CreateAccount/NewLook/NewLookConnectExchange";
import { typography } from "@shared/common/Themes/typography";
import ClientForm from "features/configuration/accounts/CreateAccount/ClientForm";
import {
  AddClientResponse,
  ClientData,
  LanguageNameSpaces,
} from "@shared/types";
import Form from "antd/es/form";
import { BOTTOM_SUMMARY_CLASS, CREATE_ACCOUNT_PATH } from "common/constants";
import { useHistory } from "react-router-dom";
import { ConnectExchangeTabs } from "@shared/features/portfolio/types";
import useConnectExchange from "@shared/features/portfolio/createAccount/useConnectExchange";
import ChangeAccountTypeMessage from "features/configuration/accounts/CreateAccount/ChangeAccountTypeMessage";
import { useBottomNavigator } from "utils/hooks/misc";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface ConnectExchangeProps {
  primaryExchange?: string;
  account?: ClientData;
  getErrorNotification?: (description: string, title?: string) => void;
  isOnboarding?: boolean;
  onSubmitSuccess?: () => void;
  showAccountTypeSwitch?: boolean;
  getSuccessMessage?: (
    response?: AddClientResponse,
    exchangesList?: string[]
  ) => void;
}

const ConnectExchange = (props: ConnectExchangeProps) => {
  const { isNewLook } = useTheme();
  if (isNewLook) return <NewLookConnectExchange {...props} />;
  return <ClassicConnectExchange {...props} />;
};

const ClassicConnectExchange = ({
  primaryExchange,
  account,
  getErrorNotification,
  isOnboarding,
  onSubmitSuccess,
  getSuccessMessage,
  showAccountTypeSwitch = true,
}: ConnectExchangeProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const history = useHistory();
  const [form] = Form.useForm();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { isExternalConnectAccount } =
    useContext(ExternalConnectAccountContext) || {};

  const { showTabs, tab, setTab, closeDrawer, onBackToSelectExchange } =
    useConnectExchange({
      primaryExchange,
      account,
    });

  const props = {
    form,
    getErrorNotification,
    isOnboarding,
    onSubmitSuccess,
    primaryExchange,
    account,
    isQuickConnect: tab === ConnectExchangeTabs.QUICK_CONNECT,
    closeDrawer,
    getSuccessMessage,
  };
  const connectExchangeForm = <ClientForm {...props} />;
  const mobilePadding =
    isBottomNavigator && !isOnboarding ? { padding: "0 12px" } : undefined;

  const getContent = () => {
    const baseDisplayChangeType =
      showAccountTypeSwitch && !account && !isExternalConnectAccount;
    if (isBottomNavigator) {
      return (
        <>
          <FormWrapper style={mobilePadding} $isOnboarding={isOnboarding}>
            {connectExchangeForm}
          </FormWrapper>
          {baseDisplayChangeType && !isOnboarding && (
            <div style={mobilePadding} className={BOTTOM_SUMMARY_CLASS}>
              <ChangeAccountTypeMessage style={{ marginTop: 16 }} />
            </div>
          )}
        </>
      );
    }
    return (
      <>
        {connectExchangeForm}
        {baseDisplayChangeType && (
          <ChangeAccountTypeMessage style={{ marginTop: 16 }} />
        )}
      </>
    );
  };

  const tabsItems = [
    {
      key: ConnectExchangeTabs.QUICK_CONNECT,
      label: t(ConnectExchangeTabs.QUICK_CONNECT),
    },
    {
      key: ConnectExchangeTabs.API_KEYS,
      label: t(ConnectExchangeTabs.API_KEYS),
    },
  ];

  const onBackClick = () => {
    history.push(CREATE_ACCOUNT_PATH);
    onBackToSelectExchange?.();
  };

  return (
    <>
      <div style={mobilePadding}>
        {!account && !isOnboarding && (
          <TitleWrapper>
            <Arrow onClick={onBackClick} />
            <Title level={3} $weight={typography.fontWeights.SEMI_BOLD}>
              {t("create_account.connect")} {primaryExchange}
            </Title>
          </TitleWrapper>
        )}
        {showTabs && (
          <Tabs
            activeKey={tab}
            onChange={(key: string) => setTab(key as ConnectExchangeTabs)}
            items={tabsItems}
          />
        )}
      </div>
      {getContent()}
    </>
  );
};

export default ConnectExchange;

const TitleWrapper = styled(Flex)`
  margin-bottom: 20px;
  justify-content: flex-start;
`;
const Arrow = styled(ArrowRightOutlined)`
  width: 24px;
  height: 24px;
  rotate: 180deg;
  margin-right: 12px;
  cursor: pointer;
  fill: ${(p) => p.theme.colors.primaryGrey};
`;
const FormWrapper = styled.div<{ $isOnboarding?: boolean }>`
  ${(p) =>
    !p.$isOnboarding &&
    `
    flex: 1;
    overflow: auto;
  `};
`;
